/** One conversion applied to an inventory item: `rate` is units of `to` per one unit of `from`. */
export type RateConversion = { from: string; to: string; rate: number };

type Dict = Record<string, unknown>;
const isDict = (value: unknown): value is Dict => Boolean(value) && typeof value === "object" && !Array.isArray(value);
const clone = <T,>(value: T): T => JSON.parse(JSON.stringify(value)) as T;
const roundMoney = (value: number) => Math.round(value * 100) / 100;

const hasPositive = (rate: unknown): boolean => {
  if (typeof rate === "number") return rate > 0;
  if (isDict(rate)) return Object.values(rate).some((value) => hasPositive(value));
  return false;
};

const scale = (value: unknown, multiplier: number): unknown => {
  if (typeof value === "number") return roundMoney(value * multiplier);
  if (isDict(value)) return Object.fromEntries(Object.entries(value).map(([key, entry]) => [key, scale(entry, multiplier)]));
  return value;
};

/** Context threaded through one conversion: target currency, known multipliers and the conversions actually used. */
type Context = { target: string; multipliers: Record<string, number>; used: Map<string, RateConversion> };
const useMultiplier = (context: Context, from: string): number | undefined => {
  const rate = context.multipliers[from];
  if (!rate || !Number.isFinite(rate) || rate <= 0) return undefined;
  context.used.set(from, { from, to: context.target, rate });
  return rate;
};

/** Fills `map[target]` from the first other currency that has a positive rate and a known multiplier. */
const fillRateMap = (map: unknown, context: Context): void => {
  if (!isDict(map) || hasPositive(map[context.target])) return;
  for (const [currency, rate] of Object.entries(map)) {
    if (currency === context.target || !hasPositive(rate)) continue;
    const multiplier = useMultiplier(context, currency);
    if (multiplier) { map[context.target] = scale(rate, multiplier); return; }
  }
};

/** Same, for the array form `[{ currency, ...rates }]` used by activity and transport tariffs. */
const fillTariffArray = (tariffs: unknown, context: Context): void => {
  if (!Array.isArray(tariffs) || tariffs.some((entry) => isDict(entry) && entry.currency === context.target && hasPositive({ ...entry, currency: undefined }))) return;
  for (const entry of tariffs) {
    if (!isDict(entry) || typeof entry.currency !== "string" || entry.currency === context.target) continue;
    const { currency, ...rates } = entry;
    if (!hasPositive(rates)) continue;
    const multiplier = useMultiplier(context, currency as string);
    if (!multiplier) continue;
    const converted = { ...(scale(rates, multiplier) as Dict), currency: context.target };
    const existing = tariffs.findIndex((item) => isDict(item) && item.currency === context.target);
    if (existing >= 0) tariffs[existing] = converted; else tariffs.push(converted);
    return;
  }
};

const fillSeasonal = (seasonal: unknown, context: Context, fill: (tariff: unknown, context: Context) => void): void => {
  if (Array.isArray(seasonal)) for (const entry of seasonal) if (isDict(entry)) fill(entry.tariff, context);
};

const fillVehicles = (vehicles: unknown, context: Context): void => {
  if (Array.isArray(vehicles)) for (const vehicle of vehicles) if (isDict(vehicle)) fillRateMap(vehicle.rate, context);
};

const finish = <T,>(item: T, context: Context): T => {
  if (context.used.size) (item as Dict).rateConversions = [...context.used.values()];
  return item;
};

/** Currencies in which `rates` hold a positive amount; used to decide which exchange rates are needed. */
const currenciesWithRates = (map: unknown): string[] => (isDict(map) ? Object.keys(map).filter((key) => hasPositive(map[key])) : []);
const currenciesInArray = (tariffs: unknown): string[] =>
  Array.isArray(tariffs) ? tariffs.flatMap((entry) => (isDict(entry) && typeof entry.currency === "string" && hasPositive({ ...entry, currency: undefined }) ? [entry.currency] : [])) : [];

/** Meal plan blocks of a hotel room: object values that carry a keyed `tariff` map. */
const mealPlanBlocks = (room: Dict): Dict[] => Object.values(room).filter((value): value is Dict => isDict(value) && isDict(value.tariff));

/**
 * Lists the other currencies an item holds rates in when it has none in `target`, so the caller can look up
 * only the exchange rates that are needed. Empty when the item already has target-currency rates.
 */
export const sourceCurrenciesNeeded = (kind: "hotel" | "activity" | "transport", item: unknown, target: string): string[] => {
  if (!isDict(item)) return [];
  const found = new Set<string>();
  const note = (currencies: string[], hasTarget: boolean) => { if (!hasTarget) currencies.forEach((currency) => found.add(currency)); };
  if (kind === "hotel") {
    for (const room of Array.isArray(item.rooms) ? item.rooms : []) {
      if (!isDict(room)) continue;
      for (const block of mealPlanBlocks(room)) {
        note(currenciesWithRates(block.tariff), hasPositive((block.tariff as Dict)[target]));
        for (const season of Array.isArray(block.seasonal) ? block.seasonal : []) if (isDict(season)) note(currenciesWithRates(season.tariff), hasPositive((season.tariff as Dict | undefined)?.[target]));
        // Imported hotels often hold rates only under a nationality group, so those need converting too.
        if (isDict(block.nationalityRates)) for (const group of Object.values(block.nationalityRates)) {
          if (!isDict(group)) continue;
          note(currenciesWithRates(group.tariff), hasPositive((group.tariff as Dict | undefined)?.[target]));
          for (const season of Array.isArray(group.seasonal) ? group.seasonal : []) if (isDict(season)) note(currenciesWithRates(season.tariff), hasPositive((season.tariff as Dict | undefined)?.[target]));
        }
      }
    }
  } else {
    const tariffs = item.tariff;
    note(currenciesInArray(tariffs), currenciesInArray(tariffs).includes(target));
    for (const season of Array.isArray(item.seasonal) ? item.seasonal : []) if (isDict(season)) note(currenciesInArray(season.tariff), currenciesInArray(season.tariff).includes(target));
  }
  for (const vehicle of Array.isArray(item.vehicles) ? item.vehicles : []) if (isDict(vehicle)) note(currenciesWithRates(vehicle.rate), hasPositive((vehicle.rate as Dict | undefined)?.[target]));
  found.delete(target);
  return [...found];
};

/**
 * Returns a copy of the hotel, activity or transport with `target`-currency rates filled in from another currency
 * wherever the target has none, using `multipliers` (target units per one unit of each source currency).
 * Rates the item already has in the target currency are never overwritten. The conversions used are recorded in
 * `rateConversions` so the quote can show where a rate came from.
 */
export const convertInventoryRates = <T,>(kind: "hotel" | "activity" | "transport", item: T, target: string, multipliers: Record<string, number>): T => {
  const result = clone(item) as unknown as Dict;
  const context: Context = { target, multipliers, used: new Map() };
  if (kind === "hotel") {
    for (const room of Array.isArray(result.rooms) ? result.rooms : []) {
      if (!isDict(room)) continue;
      for (const block of mealPlanBlocks(room)) {
        fillRateMap(block.tariff, context);
        fillSeasonal(block.seasonal, context, fillRateMap);
        if (isDict(block.nationalityRates)) for (const group of Object.values(block.nationalityRates)) {
          if (isDict(group)) { fillRateMap(group.tariff, context); fillSeasonal(group.seasonal, context, fillRateMap); }
        }
      }
    }
  } else {
    fillTariffArray(result.tariff, context);
    fillSeasonal(result.seasonal, context, fillTariffArray);
  }
  fillVehicles(result.vehicles, context);
  return finish(result, context) as unknown as T;
};

/** Short customer-safe-for-CRM note such as "Rates converted from EUR at 1.1" for a converted inventory item. */
export const describeRateConversions = (conversions?: RateConversion[]): string | null =>
  conversions?.length ? `Rates converted from ${conversions.map((entry) => `${entry.from} at ${Number(entry.rate.toFixed(4))}`).join(", ")}` : null;
